import React, { useState, useEffect, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, Users, Clock, MessageSquare, Sparkles } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface TopicConfig {
  enabled: boolean;
  weight: number;
}

export interface AIQuestionConfigData {
  enabled_topics: Record<string, TopicConfig>;
  question_style: string;
  consider_staff_on_duty: boolean;
  consider_time_of_day: boolean;
  consider_recent_reviews: boolean;
  recent_reviews_lookback_days: number;
}

interface AIQuestionConfigProps {
  businessId: string;
  config: AIQuestionConfigData;
  onChange: (config: AIQuestionConfigData) => void;
}

const TOPIC_LABELS: Record<string, { label: string; description: string }> = {
  staff_performance: {
    label: 'Staff Performance',
    description: 'Questions about employee helpfulness, friendliness, and service quality'
  },
  prices: {
    label: 'Prices & Value',
    description: 'Questions about pricing, value for money, and affordability'
  },
  busyness: {
    label: 'Busyness',
    description: 'Questions about crowd levels and busy period management'
  },
  cleanliness: {
    label: 'Cleanliness',
    description: 'Questions about premises cleanliness and hygiene'
  },
  product_quality: {
    label: 'Product Quality',
    description: 'Questions about quality of products, food, or services'
  },
  wait_time: {
    label: 'Wait Time',
    description: 'Questions about how long customers waited'
  },
  atmosphere: {
    label: 'Atmosphere',
    description: 'Questions about ambiance, music, lighting, and decor'
  },
  customer_service: {
    label: 'Customer Service',
    description: 'Questions about overall service experience'
  },
};

const QUESTION_STYLES = [
  { value: 'conversational', label: 'Conversational', description: 'Friendly and casual tone' },
  { value: 'professional', label: 'Professional', description: 'Formal and business-like' },
  { value: 'enthusiastic', label: 'Enthusiastic', description: 'Energetic and positive' },
  { value: 'direct', label: 'Direct', description: 'Straight to the point' },
];

export const DEFAULT_AI_CONFIG: AIQuestionConfigData = {
  enabled_topics: {
    staff_performance: { enabled: true, weight: 5 },
    prices: { enabled: true, weight: 3 },
    busyness: { enabled: true, weight: 4 },
    cleanliness: { enabled: true, weight: 5 },
    product_quality: { enabled: true, weight: 5 },
    wait_time: { enabled: true, weight: 4 },
    atmosphere: { enabled: true, weight: 3 },
    customer_service: { enabled: true, weight: 5 },
  },
  question_style: 'conversational',
  consider_staff_on_duty: true,
  consider_time_of_day: true,
  consider_recent_reviews: true,
  recent_reviews_lookback_days: 7,
};

export const AIQuestionConfig: React.FC<AIQuestionConfigProps> = ({
  businessId,
  config,
  onChange,
}) => {
  const updateTopicEnabled = (topic: string, enabled: boolean) => {
    onChange({
      ...config,
      enabled_topics: {
        ...config.enabled_topics,
        [topic]: { ...config.enabled_topics[topic], enabled },
      },
    });
  };

  const updateTopicWeight = (topic: string, weight: number) => {
    onChange({
      ...config,
      enabled_topics: {
        ...config.enabled_topics,
        [topic]: { ...config.enabled_topics[topic], weight },
      },
    });
  };

  const enabledTopicsCount = Object.values(config.enabled_topics).filter(t => t.enabled).length;

  return (
    <div className="space-y-6">
      {/* Topics Configuration */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Question Topics
            <Badge className="ml-auto">
              {enabledTopicsCount} active
            </Badge>
          </CardTitle>
          <CardDescription>
            Select which topics AI can ask about and set priority weights (1-10)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(TOPIC_LABELS).map(([topic, { label, description }]) => {
            const topicConfig = config.enabled_topics[topic] || { enabled: true, weight: 5 };
            return (
              <div key={topic} className="flex items-center gap-4 p-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors">
                <Switch
                  checked={topicConfig.enabled}
                  onCheckedChange={(checked) => updateTopicEnabled(topic, checked)}
                />
                <div className="flex-1 min-w-0">
                  <Label className="font-medium">{label}</Label>
                  <p className="text-xs text-muted-foreground truncate">{description}</p>
                </div>
                <div className="flex items-center gap-3 w-32">
                  <Slider
                    value={[topicConfig.weight]}
                    onValueChange={([value]) => updateTopicWeight(topic, value)}
                    min={1}
                    max={10}
                    step={1}
                    disabled={!topicConfig.enabled}
                    className="flex-1"
                  />
                  <span className={`text-sm font-mono w-6 text-center ${!topicConfig.enabled ? 'text-muted-foreground' : ''}`}>
                    {topicConfig.weight}
                  </span>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Question Style */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            Question Style
          </CardTitle>
          <CardDescription>
            Choose the tone and style for generated questions
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select
            value={config.question_style}
            onValueChange={(value) => onChange({ ...config, question_style: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {QUESTION_STYLES.map((style) => (
                <SelectItem key={style.value} value={style.value}>
                  <div>
                    <span className="font-medium">{style.label}</span>
                    <span className="text-muted-foreground ml-2">- {style.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Context Settings */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Context Settings
          </CardTitle>
          <CardDescription>
            Configure what context AI considers when generating questions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div className="flex items-center gap-3">
              <Users className="h-4 w-4 text-muted-foreground" />
              <div>
                <Label>Consider Staff on Duty</Label>
                <p className="text-xs text-muted-foreground">
                  AI may ask about specific employees currently working
                </p>
              </div>
            </div>
            <Switch
              checked={config.consider_staff_on_duty}
              onCheckedChange={(checked) => onChange({ ...config, consider_staff_on_duty: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div className="flex items-center gap-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <Label>Consider Time of Day</Label>
                <p className="text-xs text-muted-foreground">
                  Questions adapt to morning/afternoon/evening context
                </p>
              </div>
            </div>
            <Switch
              checked={config.consider_time_of_day}
              onCheckedChange={(checked) => onChange({ ...config, consider_time_of_day: checked })}
            />
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg border">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-4 w-4 text-muted-foreground" />
              <div>
                <Label>Avoid Recent Topics</Label>
                <p className="text-xs text-muted-foreground">
                  Reduce repetition by checking recent feedback
                </p>
              </div>
            </div>
            <Switch
              checked={config.consider_recent_reviews}
              onCheckedChange={(checked) => onChange({ ...config, consider_recent_reviews: checked })}
            />
          </div>

          {config.consider_recent_reviews && (
            <div className="pl-11 space-y-2">
              <Label className="text-sm">Lookback Period</Label>
              <Select
                value={config.recent_reviews_lookback_days.toString()}
                onValueChange={(value) => onChange({ ...config, recent_reviews_lookback_days: parseInt(value) })}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">Last 3 days</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="14">Last 14 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Hook to load and save AI config
export function useAIQuestionConfig(businessId: string | null) {
  const [config, setConfig] = useState<AIQuestionConfigData>(DEFAULT_AI_CONFIG);
  const [configId, setConfigId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConfig = useCallback(async () => {
    if (!businessId) return;

    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('ai_question_configs')
        .select('*')
        .eq('business_id', businessId)
        .single();

      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          // No config found - use defaults
          setConfig(DEFAULT_AI_CONFIG);
          setConfigId(null);
        } else {
          throw fetchError;
        }
      } else {
        setConfig({
          enabled_topics: data.enabled_topics || DEFAULT_AI_CONFIG.enabled_topics,
          question_style: data.question_style || DEFAULT_AI_CONFIG.question_style,
          consider_staff_on_duty: data.consider_staff_on_duty ?? DEFAULT_AI_CONFIG.consider_staff_on_duty,
          consider_time_of_day: data.consider_time_of_day ?? DEFAULT_AI_CONFIG.consider_time_of_day,
          consider_recent_reviews: data.consider_recent_reviews ?? DEFAULT_AI_CONFIG.consider_recent_reviews,
          recent_reviews_lookback_days: data.recent_reviews_lookback_days || DEFAULT_AI_CONFIG.recent_reviews_lookback_days,
        });
        setConfigId(data.id);
      }
    } catch (err) {
      console.error('Error loading AI config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const saveConfig = useCallback(async (newConfig: AIQuestionConfigData): Promise<string | null> => {
    if (!businessId) return null;

    setLoading(true);
    try {
      const { data, error: saveError } = await supabase
        .from('ai_question_configs')
        .upsert({
          ...(configId && { id: configId }),
          business_id: businessId,
          ...newConfig,
        }, {
          onConflict: 'business_id'
        })
        .select('id')
        .single();

      if (saveError) throw saveError;

      setConfigId(data.id);
      return data.id;
    } catch (err) {
      console.error('Error saving AI config:', err);
      setError(err instanceof Error ? err.message : 'Failed to save config');
      return null;
    } finally {
      setLoading(false);
    }
  }, [businessId, configId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  return {
    config,
    setConfig,
    configId,
    loading,
    error,
    saveConfig,
    loadConfig,
  };
}
